import React from "react";

import classnames from "classnames";
import _ from "underscore";

import { Popover } from "Cobalt/Popover";
import BodyText from "Common/BodyText";
import SIOverlayTrigger from "Common/SIOverlayTrigger";

import makeBem from "Utils/makeBem";

const bem = makeBem("InfoPopover");

type Props = {
  infoTitle?: React.ReactNode;
  infoText: React.ReactNode;
  triggerText: string;
  className?: string;
  htmlFor?: string;
  /**
   * NOTE: displayComponent must support ref forwarding, e.g. a component wrapped in React.forwardRef or withStyleProps
   * If you continue to have trouble, a common workaround is to wrap displayComponent in a <span>.
   * */
  displayComponent?: React.ReactElement<any>;
  placement?: string;
};

const InfoPopover = ({ infoTitle, infoText, triggerText, className, displayComponent, htmlFor, placement, ...rest }: Props) => {
  const popoverTitle = infoTitle ? (
    <BodyText className="left">
      <span className={bem("Title")}>{infoTitle}</span>
    </BodyText>
  ) : null;

  const popoverDisplay = (
    <Popover showBackdrop={false} id={_.uniqueId("infoPopover")}>
      {popoverTitle && <Popover.Header>{popoverTitle}</Popover.Header>}
      <Popover.Body>
        <BodyText className="left">{infoText}</BodyText>
      </Popover.Body>
    </Popover>
  );

  // BodyText does not support ref forwarding, which is required for popover positioning.
  // Use a plain <span> instead.
  const triggerElement = displayComponent || (
    <span className={classnames("BodyText", "BodyText--small", "BodyText--normal", bem(), className, "SIOverlayTrigger__base", "ml-1")} tabIndex={0}>
      ({triggerText})
    </span>
  );

  return (
    <SIOverlayTrigger
      trackType="common-questions"
      trackId={typeof htmlFor === "string" ? htmlFor : ""}
      trigger={["hover", "focus"]}
      overlay={popoverDisplay}
      placement={placement}
      {...rest}
    >
      {triggerElement}
    </SIOverlayTrigger>
  );
};

InfoPopover.defaultProps = {
  triggerText: "More",
};

export default InfoPopover;
